import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  BadgeVerificationData,
  TicketChartData,
  VehicleChartData,
  RealTimeStats,
  AnalyticsParams,
  AnalyticsResponse,
  ExhibitorBadgeStats,
} from "../Component/Dashboard/Dashboard/types/dashboard"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
})

// Attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/login"
    }
    return Promise.reject(error)
  },
)

const DashboardService = {
  // Get exhibitor statistics
  async getExhibitorStats(): Promise<{ totalExhibitors: number }> {
    try {
      const response = await api.get("/api/stakeholders/dashboard-stats/")
      return response.data
    } catch (error) {
      console.error("Error fetching exhibitor stats:", error)
      throw new Error("Failed to fetch exhibitor statistics")
    }
  },

  // Get badge statistics by exhibitor type
  async getBadgeStatsByExhibitorType(): Promise<ExhibitorBadgeStats> {
    try {
      const response = await api.get("/api/badges/exhibitor-type-stats/")
      return response.data.data
    } catch (error) {
      console.error("Error fetching badge stats by exhibitor type:", error)
      throw new Error("Failed to fetch badge statistics by exhibitor type")
    }
  },

  // Get badge statistics
  async getBadgeStats(): Promise<{
    totalBadges: { total: number; free: number; extra: number }
    badgePayments: {
      totalAmount: number
      paidCount: number
      freeCount: number
      waivedCount: number
      pendingCount: number
    }

    extraBadgeAmount: {
      totalAmount: number
      badgeFee: number
    }
    freeBadges: { count: number }
    reprintStats: { totalAmount: number; count: number }
    exceedsStats: { totalAmount: number; count: number }
    hostBadges: { totalAmount: number; count: number }
  }> {
    try {
      const response = await api.get("/api/badges/dashboard-stats/")
      return response.data
    } catch (error) {
      console.error("Error fetching badge stats:", error)
      throw new Error("Failed to fetch badge statistics")
    }
  },

  // Get entry statistics (tickets and vehicles)
  async getEntryStats(): Promise<{
    ticketStats: {
      totalAmount: number
      totalTickets: number
      usedTickets: number
      unusedTickets: number
    }
    vehicleStats: {
      totalAmount: number
      totalVisits: number
      activeVisitors: number
    }
  }> {
    try {
      const response = await api.get("/api/entries/dashboard-stats/")
      return response.data
    } catch (error) {
      console.error("Error fetching entry stats:", error)
      throw new Error("Failed to fetch entry statistics")
    }
  },

  // Get chart data for badges
  async getBadgeChartsData(days = 30): Promise<BadgeVerificationData[]> {
    try {
      const response = await api.get(`/api/badges/chart-data/?days=${days}`)
      return response.data
    } catch (error) {
      console.error("Error fetching badge charts data:", error)
      return []
    }
  },

  // Get chart data for entries
  async getEntryChartsData(days = 30): Promise<{
    ticketTrends: TicketChartData[]
    vehicleTrends: VehicleChartData[]
  }> {
    try {
      const response = await api.get(`/api/entries/chart-data/?days=${days}`)
      return response.data
    } catch (error) {
      console.error("Error fetching entry charts data:", error)
      return { ticketTrends: [], vehicleTrends: [] }
    }
  },

  // Get real-time updates
  async getRealTimeStats(): Promise<RealTimeStats> {
    try {
      const response = await api.get("/api/entries/realtime-stats/")
      return response.data as RealTimeStats
    } catch (error) {
      console.error("Error fetching real-time stats:", error)
      throw new Error("Failed to fetch real-time statistics")
    }
  },

  async getAnalyticsComparison(params?: AnalyticsParams): Promise<AnalyticsResponse> {
    try {
      const queryParams = new URLSearchParams()

      if (params?.start_date) {
        queryParams.append("start_date", params.start_date)
      }

      if (params?.end_date) {
        queryParams.append("end_date", params.end_date)
      }

      const url = `/api/entries/analytics-comparison/${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
      const response = await api.get(url)

      return response.data as AnalyticsResponse
    } catch (error) {
      console.error("Error fetching analytics comparison:", error)
      throw new Error("Failed to fetch analytics comparison data")
    }
  },

  // Export dashboard data
  async exportDashboardData(format: "csv" | "pdf" = "csv"): Promise<Blob> {
    try {
      const response = await api.get(`/api/dashboard/export/?format=${format}`, {
        responseType: "blob",
      })
      return response.data
    } catch (error) {
      console.error("Error exporting dashboard data:", error)
      throw new Error("Failed to export dashboard data")
    }
  },
}

export default DashboardService
